import { updateShippingAddress } from "@/api/rest/authenticated/Address";
import { ShippingAddress } from "@/components/Checkout/types";
import { StoreError } from "@/components/StoreComponents/StoreError";
import { useEffect, useState } from "react";

import { isAddressEqual } from "@/components/Checkout/components/CheckoutSummary/CheckoutEditShippingForm";
import { useTrackActions } from "@/utils/hooks/useTracking";
import { AnalyticsTrackingEvent } from "@brandclub/common-ui";
import { Skeleton } from "@mui/material";
import { useCheckoutUI } from "../../../CheckoutUIProvider";
import { useCheckoutCartInfo } from "../../../hooks/useCheckoutCartInfo";
import { CheckoutIntent } from "../../../types/CheckoutIntent";
import CheckoutEditShippingForm from "./CheckoutEditShippingForm";

export const ShippingMethodForm = () => {
  const [machineState, send] = useCheckoutUI();
  const [trackAction] = useTrackActions();
  const { items } = useCheckoutCartInfo();

  const [addressError, setAddressError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  // @ts-ignore
  const { checkoutIntent, userRetailerCheckoutOrder } = machineState.context;
  const checkoutIntentId = checkoutIntent?.checkoutIntentId;
  const orderId = userRetailerCheckoutOrder?.orderId;
  const isPostSubmission = !!orderId;

  useEffect(() => {
    if (checkoutIntentId) {
      trackAction(AnalyticsTrackingEvent.CHECKOUT_STEP_VIEWED, {
        checkoutIntentId: checkoutIntentId,
        stepName: "shipping",
        viewType: "editShippingAddress",
        checkoutItems: items || [],
        checkoutPhase: isPostSubmission ? "post_submission" : "pre_submission",
        ...(orderId && { orderId })
      });
    }
  }, [trackAction, checkoutIntentId, isPostSubmission, orderId, items]);

  const handleValidAddress = async ({
    shippingInput,
    skipValidation = false,
  }: {
    shippingInput: CheckoutIntent["shippingAddress"];
    skipValidation?: boolean;
  }) => {
    if (!shippingInput || !checkoutIntent?.checkoutIntentId) return;

    const { checkoutIntent: updatedCheckoutIntent, status } =
      await updateShippingAddress({
        shippingAddress: shippingInput,
        checkoutIntentId: checkoutIntent.checkoutIntentId,
        skipValidation,
      });

    if (status === "success") {
      // First update checkout intent data
      send({
        type: "SET_CHECKOUT_INTENT",
        data: { checkoutIntent: updatedCheckoutIntent },
      });

      // Track successful completion
      trackAction(AnalyticsTrackingEvent.CHECKOUT_STEP_COMPLETED, {
        checkoutIntentId: checkoutIntentId,
        stepName: "shipping",
        viewType: "editShippingAddress",
        checkoutItems: items || [],
        checkoutPhase: isPostSubmission ? "post_submission" : "pre_submission",
        ...(orderId && { orderId })
      });

      // Mark shipping section as valid and completed
      send({ type: "SET_IS_VALID_ADDRESS", data: true });
    } else {
      const errorMessage = "An error occurred while updating your address. Please try again.";
      setAddressError(errorMessage);

      // Track error
      trackAction(AnalyticsTrackingEvent.CHECKOUT_STEP_ERROR, {
        checkoutIntentId: checkoutIntentId,
        stepName: "shipping",
        viewType: "editShippingAddress",
        checkoutItems: items || [],
        errorMessage: errorMessage,
        errorType: "apiValidation",
        checkoutPhase: isPostSubmission ? "post_submission" : "pre_submission",
        ...(orderId && { orderId })
      });
    }
  };

  const handleSaveAddress = async (event: {
    address: Partial<ShippingAddress>;
    skipValidation?: boolean;
  }) => {
    // Clear previous errors
    setAddressError(null);

    const { address, skipValidation } = event;

    const { firstName, lastName, address1, city, state, zipCode } =
      address || {};

    // Basic form validation
    if (!address1 || !city || !state || !zipCode || !firstName || !lastName) {
      const errorMessage = "Please fill in all required address fields";
      setAddressError(errorMessage);

      // Track validation error
      trackAction(AnalyticsTrackingEvent.CHECKOUT_STEP_ERROR, {
        checkoutIntentId: checkoutIntentId,
        stepName: "shipping",
        viewType: "editShippingAddress",
        checkoutItems: items || [],
        errorMessage: errorMessage,
        errorType: "validation",
        checkoutPhase: isPostSubmission ? "post_submission" : "pre_submission",
        ...(orderId && { orderId })
      });
      return;
    }

    try {
      setLoading(true);
      send({ type: "SET_IS_VALID_ADDRESS", data: true });

      await handleValidAddress({
        shippingInput: address as ShippingAddress,
        skipValidation,
      });
    } catch (error) {
      const errorMessage = "An error occurred while validating your address. Please try again.";
      setAddressError(errorMessage);
      console.error("Address validation error:", error);

      // Track error
      trackAction(AnalyticsTrackingEvent.CHECKOUT_STEP_ERROR, {
        checkoutIntentId: checkoutIntentId,
        stepName: "shipping",
        viewType: "editShippingAddress",
        checkoutItems: items || [],
        errorMessage: errorMessage,
        errorType: "catch",
        checkoutPhase: isPostSubmission ? "post_submission" : "pre_submission",
        ...(orderId && { orderId })
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAddress = async (
    data: Partial<CheckoutIntent["shippingAddress"]>
  ) => {
    setLoading(true);
    if (
      checkoutIntent &&
      !isAddressEqual(checkoutIntent?.shippingAddress, data as ShippingAddress)
    ) {
      // Clear error when user updates any field
      setAddressError(null);
      send({ type: "RESET_IS_VALID_ADDRESS" });
      send({ type: "SET_SHIPPING", data });
      await handleValidAddress({
        shippingInput: data as ShippingAddress,
        skipValidation: !(data as ShippingAddress).isValid,
      });
    }
    setLoading(false);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
      {loading ? (
        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          <Skeleton
            variant="rectangular"
            height={25}
            sx={{ backgroundColor: "#F6F8FA" }}
          />
          <Skeleton
            variant="rectangular"
            height={25}
            sx={{ backgroundColor: "#F6F8FA" }}
          />
        </div>
      ) : (
        <CheckoutEditShippingForm
          loading={loading}
          availableAddresses={
            (checkoutIntent?.availableShippingAddresses ??
              []) as ShippingAddress[]
          }
          handleSaveShippingAddress={handleSaveAddress}
          handleSelectShippingAddress={handleSelectAddress}
          currentAddress={
            checkoutIntent?.shippingAddress
              ? ({
                ...checkoutIntent?.shippingAddress,
                extendedAttributes: {
                  isSelected: true,
                },
              } as ShippingAddress)
              : undefined
          }
          onCancel={() => {
            send({ type: "CLOSE", data: { section: "shipping" } });
          }}
        />
      )}
      {addressError && <StoreError errorMessage={addressError} />}
    </div>
  );
};
